// Locate a value inside an upload's full extracted text. The number-matching is
// deliberately format-tolerant: "163320" must still match "163,320" / "163 320"
// in the source, so digit runs are located with optional thousands separators.

export interface TextMatch {
  // Character offsets into the ORIGINAL text (not a normalised copy), so the
  // caller can slice / highlight the real string.
  start: number;
  end: number;
}

const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Build a regex that matches a run of digits with OPTIONAL single thousands
// separators (comma / space / thin-space) between them, so "163320" locates
// "163,320". Decimal points are matched literally. Returns null when the needle
// carries fewer than two digits (nothing distinctive enough to fuzzy-match - a
// single digit would match the first stray digit anywhere).
//
// The pattern is DIGIT-BOUNDARY guarded so a value's digit run only matches when
// it's a whole number, not a substring of a longer one: "800" must NOT match
// inside "1,800", nor "163320" inside "9163320". The leading boundary is a
// consumed group `(?:^|[^\d.,])` - NOT a lookbehind: WebKit only shipped regex
// lookbehind in Safari 16.4, and the app has no error boundary, so a lookbehind
// `SyntaxError` at construction would blank the whole tree on older iOS/Safari.
// The number itself is capture GROUP 1 (the caller subtracts the consumed
// boundary char back off). The trailing `(?![\d])(?!\.\d)` lookaheads (which are
// universally supported) reject a following digit / decimal continuation, so
// "12" doesn't match inside "12.5" while a sentence-ending period still matches.
const numberRegex = (needle: string): RegExp | null => {
  const digits = needle.replace(/[^0-9.]/g, "");
  if (digits.replace(/\./g, "").length < 2) return null;
  // Interleave each character with an optional separator. A decimal point stays
  // literal; digits allow an optional [,\s] before them.
  const body = digits
    .split("")
    .map((ch, i) => {
      if (ch === ".") return "\\.";
      return i === 0 ? ch : `[,\\s\\u202f\\u00a0]?${ch}`;
    })
    .join("");
  return new RegExp(`(?:^|[^\\d.,])(${body})(?![\\d])(?!\\.\\d)`);
};

// Format a number without floating-point artefacts or trailing zeros
// (163.32000000000002 -> "163.32", 1710 -> "1710"). Returns "" for non-finite.
const trimNumber = (n: number): string => {
  if (!isFinite(n)) return "";
  // Up to 6 significant decimals is plenty for a kW<->MW<->GW rescale; parseFloat
  // then drops any trailing zeros the fixed notation introduces.
  return String(parseFloat(n.toFixed(6)));
};

// Candidate string forms of a numeric value to try locating, covering a
// UNIT-SCALE mismatch between the stored value and the source text: a cap stored
// as 163320 (kW) is routinely written "163.32 MW" in the thread, so the raw
// digits never match. The stored caps are always in the smaller unit (kW), so we
// only DOWN-scale: the value as-is plus kW->MW (/1e3) and kW->GW (/1e6). We do
// NOT up-scale (a kW value written in a smaller unit doesn't occur, and it would
// synthesise huge numbers that false-match unrelated text). Each variant still
// needs >= 2 digits (the numberRegex floor), so a rescale to a single digit is
// skipped.
const numberVariants = (value: string): string[] => {
  const n = Number(value.replace(/[^0-9.eE+-]/g, ""));
  if (!isFinite(n) || n === 0) return [value];
  const out = new Set<string>([value]);
  for (const factor of [1e3, 1e6]) {
    const s = trimNumber(n / factor);
    if (s && s.replace(/[^0-9]/g, "").length >= 2) out.add(s);
  }
  return [...out];
};

// Locate a quote in `text` tolerant of whitespace/punctuation differences: match
// its word tokens (letters/digits, Unicode-aware) IN ORDER, with a lenient
// separator between them. This bridges the gap between a stored evidence quote
// and the raw source text when the model drops formatting the source carries:
//  - bullets/newlines re-rendered as commas ("...repairs, Cleaning..." vs
//    "...repairs\nCleaning..."), AND
//  - list-marker tokens the quote omits ("...repairs\n(o) Cleaning..." - the "o"
//    / "*" sub-bullet), handled by letting the separator swallow isolated 1-2
//    char tokens between the quote's words.
// Returns null when the quote has too few tokens to be distinctive, or its words
// don't appear in order (a genuine paraphrase - nothing verbatim to highlight).
const _FUZZY_SEP = "[^\\p{L}\\p{N}]+(?:[\\p{L}\\p{N}]{1,2}[^\\p{L}\\p{N}]+)*";
const locateFuzzy = (text: string, evidence: string | null | undefined): TextMatch | null => {
  const trimmed = (evidence ?? "").trim();
  if (trimmed.length < 4) return null;
  const tokens = trimmed.match(/[\p{L}\p{N}]+/gu);
  if (!tokens || tokens.length < 2) return null;
  const pattern = tokens.map(escapeRegExp).join(_FUZZY_SEP);
  try {
    const m = new RegExp(pattern, "iu").exec(text);
    return m ? { start: m.index, end: m.index + m[0].length } : null;
  } catch {
    // A pathological quote that can't compile - degrade to "no match".
    return null;
  }
};

// Find the best location of `value` (and/or its `evidence` quote) inside `text`.
// Tries, in order: the evidence quote (exact, then whitespace/punctuation-
// tolerant), then a format-tolerant number match on the value, then a plain
// case-insensitive substring of the value. Returns null when nothing locates.
export const locateValueInText = (
  text: string,
  opts: { value?: string | null; evidence?: string | null },
): TextMatch | null => {
  if (!text) return null;

  const tryExact = (needle: string | null | undefined): TextMatch | null => {
    const n = (needle ?? "").trim();
    if (n.length < 2) return null;
    const idx = text.toLowerCase().indexOf(n.toLowerCase());
    return idx >= 0 ? { start: idx, end: idx + n.length } : null;
  };

  // 1. The evidence quote the extractor returned - the tightest anchor. Try an
  // exact match first, then fall back to a WHITESPACE/PUNCTUATION-TOLERANT match:
  // the model routinely re-renders a bulleted list as comma-separated prose or
  // collapses newlines, so a stored quote like "...repairs, Cleaning..." isn't a
  // byte-exact substring of a source that reads "...repairs\nCleaning...". We
  // match the quote's word tokens IN ORDER separated by any run of non-word
  // characters, so newline<->comma<->bullet differences don't defeat the deep
  // link. A genuine paraphrase (different words) still won't match, which is
  // correct - there's nothing verbatim to highlight.
  const byEvidence = tryExact(opts.evidence) ?? locateFuzzy(text, opts.evidence);
  if (byEvidence) return byEvidence;

  const value = (opts.value ?? "").trim();

  // 2. A TEXTUAL value (a model code "JAM66D45-640/LB", "DEMG 515w", "JA Solar")
  // must be matched as the WHOLE string first - otherwise step 3's number match
  // would highlight only its embedded digits ("515" out of "DEMG 515w"). Gate on
  // the value containing a letter: a purely numeric value skips this and goes
  // straight to the boundary-safe number match (so "800" can't spuriously match
  // inside "1,800"). Exact, then whitespace/punctuation-tolerant.
  if (/\p{L}/u.test(value)) {
    const byValueStr = tryExact(value) ?? locateFuzzy(text, value);
    if (byValueStr) return byValueStr;
  }

  // 3. Format-tolerant number match (163320 -> "163,320"), tried across
  // unit-scale variants (163320 kW -> "163.32" MW) so a kW/MW/GW mismatch
  // between the stored value and the source text still locates.
  for (const variant of numberVariants(value)) {
    const re = numberRegex(variant);
    const m = re?.exec(text);
    if (m) {
      // Group 1 is the number; m[0] also holds the consumed leading boundary
      // char (empty at string start), so subtract it back off for the offset.
      const num = m[1];
      const start = m.index + (m[0].length - num.length);
      return { start, end: start + num.length };
    }
  }

  // No safe match. We deliberately do NOT fall back to a plain substring of the
  // value here: a textual value already tried exact+fuzzy above, and a purely
  // numeric value must not bypass the number match's digit-boundary guard (a
  // plain indexOf of "800" would wrongly match inside "1,800").
  return null;
};

// The page header the extractor writes into a PDF's text; must agree with
// page_chunking.PAGE_HEADER_RE on the backend.
const PAGE_HEADER_RE = /^--- page (\d+) ---$/gm;

// The 1-based PDF page an offset in the extracted text falls on: the last page
// header before it, or page 1 when none precedes it. Null when the text carries no
// page headers at all (not a PDF's text).
export const pageAtOffset = (text: string, offset: number): number | null => {
  let page: number | null = null;
  let sawHeader = false;
  for (const m of text.matchAll(PAGE_HEADER_RE)) {
    sawHeader = true;
    if (m.index > offset) break;
    page = Number(m[1]);
  }
  if (!sawHeader) return null;
  return page ?? 1;
};
